/**
 * Text segment modes (W1-10a; DESIGN.md §6.8, rpe-format F§9.3, rendering R§3.6).
 *
 * A text segment runs from key k (start string k.vOut) to the next key (end string next.vIn). Phira's string tween
 * picks its behaviour from the two strings, so a mode is a pair of strings (plus a curve that animates):
 *   switch   shows `from` until the next key, then `to`        (a Stay segment: hold arrives as a jump)
 *   typeOn   "" -> text, reveals round(t * len) characters      (no overshooting curve: Phira panics)
 *   erase    text -> "", removes characters                      (same guard)
 *   extend   one string is a prefix of the other                 (overshoot allowed; the checker warns)
 *   counter  "%P%<from>" -> "%P%<to>", an animated number; the end key's leave value becomes the plain number
 *            when the end key is the last key or was continuous, so Phira never shows a literal %P% afterwards.
 * A Stay curve on an animated mode becomes Linear; an overshooting curve on type-on / erase becomes Linear (noted).
 * A linked segment is unlinked first (its partners would silently change too; noted).
 */

import { Ease as EaseApi, HOLD, LINEAR } from '../ease/index';
import type { Ease } from '../ease/types';
import type { Key } from '../model/types';
import { keyOf, setKeyOut, trackOf, unlinkAround, type Tx } from './structureKit';
import type { LineCommands } from './types';

export type TextSegmentMode = 'switch' | 'typeOn' | 'erase' | 'extend' | 'counter';

/** Marker Phira uses for animated numbers (F§9.3). */
export const COUNTER_MARK = '%P%';

const NUMBER_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/** The plain number of a counter end ("%P%12" -> "12"), or null when the text is not numeric. */
export function counterNumber(s: string): string | null {
  const plain = s.split(COUNTER_MARK).join('').trim();
  return NUMBER_RE.test(plain) ? plain : null;
}

/** Mode Phira derives from a segment's two strings (DESIGN.md §6.8; hold = switch). */
export function textSegmentMode(start: string, end: string, ease: Ease): TextSegmentMode {
  if (ease.kind === 'hold') return 'switch';
  if (start.includes(COUNTER_MARK) && end.includes(COUNTER_MARK)) return 'counter';
  if (start === end) return 'switch';
  if (start === '') return 'typeOn';
  if (end === '') return 'erase';
  if (end.startsWith(start) || start.startsWith(end)) return 'extend';
  return 'switch';
}

/** Curve for an animated mode: Stay becomes Linear; overshooting curves become Linear on type-on / erase. */
function animatedEase(tx: Tx, ease: Ease, guard: boolean): Ease {
  if (ease.kind === 'hold') return LINEAR;
  if (guard && EaseApi.validateFor(ease, 'textRevealErase') !== null) {
    tx.note({ key: 'cmd.text.easeReplaced' });
    return LINEAR;
  }
  return ease;
}

const setTextSegmentMode: LineCommands['setTextSegmentMode'] = (tx: Tx, { segment, mode, from, to }) => {
  const k0 = keyOf(tx, segment);
  const track = trackOf(tx, k0.track);
  if (track.owner.kind !== 'line' || track.owner.prop !== 'text') tx.fail({ key: 'cmd.text.notText' });
  const nextId = tx.idx.nextKey(segment);
  if (nextId === undefined) tx.fail({ key: 'cmd.text.noSegment' });
  const unlinkedHere = unlinkAround(tx, k0);
  const unlinkedNext = unlinkNext(tx, nextId);
  if (unlinkedHere || unlinkedNext) tx.note({ key: 'cmd.text.unlinked' });
  const k = tx.doc.keys.get(segment) as Key;
  const next = tx.doc.keys.get(nextId) as Key;
  const s = k.vOut as string;
  const e = next.vIn as string;
  const nextContinuous = next.vIn === next.vOut;
  const nextIsLast = tx.idx.nextKey(nextId) === undefined;

  let start: string;
  let end: string;
  let after: string | null = null;
  let ease: Ease;
  switch (mode) {
    case 'switch': {
      start = from ?? s;
      end = start;
      after = to ?? (nextContinuous ? e : (next.vOut as string));
      ease = HOLD;
      break;
    }
    case 'typeOn': {
      start = '';
      end = to ?? (e !== '' ? e : s);
      if (end === '') tx.fail({ key: 'cmd.text.emptyText' });
      ease = animatedEase(tx, k.ease, true);
      break;
    }
    case 'erase': {
      start = from ?? (s !== '' ? s : e);
      end = '';
      if (start === '') tx.fail({ key: 'cmd.text.emptyText' });
      ease = animatedEase(tx, k.ease, true);
      break;
    }
    case 'extend': {
      start = from ?? s;
      end = to ?? e;
      if (start === end || !(end.startsWith(start) || start.startsWith(end))) {
        tx.fail({ key: 'cmd.text.extendNeedsPrefix' });
      }
      ease = animatedEase(tx, k.ease, false);
      break;
    }
    case 'counter': {
      const a = counterNumber(from ?? s) ?? (from === undefined && s === '' ? '0' : null);
      const b = counterNumber(to ?? e) ?? (to === undefined && e === '' ? '0' : null);
      if (a === null || b === null) tx.fail({ key: 'cmd.text.counterNotNumeric' });
      start = COUNTER_MARK + a;
      end = COUNTER_MARK + b;
      const nextOut = next.vOut as string;
      if (nextIsLast || (nextContinuous && !nextOut.includes(COUNTER_MARK))) after = b;
      else if (nextContinuous) after = end;
      ease = animatedEase(tx, k.ease, false);
      break;
    }
  }

  if (ease !== k.ease) tx.update('keys', segment, { ease });
  setKeyOut(tx, tx.doc.keys.get(segment) as Key, start);
  // The end string is the next key's arrive value; with a hold it already follows the start (setKeyOut).
  const nextNow = tx.doc.keys.get(nextId) as Key;
  if (ease.kind !== 'hold' && nextNow.vIn !== end) tx.update('keys', nextId, { vIn: end });
  let leave: string | null = after;
  if (leave === null && nextContinuous && mode !== 'switch') leave = end;
  if (leave !== null && leave !== (tx.doc.keys.get(nextId) as Key).vOut) {
    setKeyOut(tx, tx.doc.keys.get(nextId) as Key, leave);
  }
};

function unlinkNext(tx: Tx, nextId: Key['id']): boolean {
  const next = tx.doc.keys.get(nextId);
  if (next === undefined || next.link === 0) return false;
  tx.update('keys', nextId, { link: 0 });
  return true;
}

export const textCommands = { setTextSegmentMode } as const;
